// Insert text into a React controlled <textarea> or <input> at a specific
// selection, firing a real React onChange.
//
// Why the native prototype setter: React tracks the value it last rendered and
// silently swallows `el.value = x` assignments made directly against the
// instance (the "cannot update a component while rendering" / no-op problem).
// Calling the NATIVE setter from the prototype bypasses React's instance
// override, so the DOM value actually changes; dispatching a bubbling "input"
// event then makes React's onChange fire with the new value, updating the
// controlled component's state. This is the standard reliable pattern for
// programmatic insertion into controlled inputs (used by autofill libraries).

type TextInputElement = HTMLTextAreaElement | HTMLInputElement;

export function insertTextAtCursor(
  el: TextInputElement,
  text: string,
  selectionStart?: number,
  selectionEnd?: number,
): void {
  const start = selectionStart ?? el.selectionStart ?? el.value.length;
  const end = selectionEnd ?? el.selectionEnd ?? start;
  const before = el.value.slice(0, start);
  const after = el.value.slice(end);
  const next = before + text + after;

  // Use the native value setter (from the prototype, not the instance) so
  // React's override doesn't swallow the assignment.
  const proto =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
  if (descriptor?.set) {
    descriptor.set.call(el, next);
  } else {
    el.value = next;
  }

  // Fire a real bubbling input event so React's onChange handler runs and the
  // controlled component's state updates.
  el.dispatchEvent(new Event("input", { bubbles: true }));

  // Place the cursor after the inserted text.
  const caret = start + text.length;
  try {
    el.setSelectionRange(caret, caret);
  } catch {
    // Some input types (e.g. number) don't support setSelectionRange — ignore.
  }
  el.focus({ preventScroll: true });
}
